import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  FiMessageCircle, FiPhone, FiVideo, FiUsers, FiBarChart2,
  FiShield, FiTarget, FiGlobe, FiCheck, FiArrowRight,
  FiLayers, FiTrendingUp, FiSmartphone, FiHexagon, FiPlay,
  FiZap, FiRefreshCw, FiCamera, FiMic, FiMail, FiCalendar,
  FiCpu, FiDollarSign, FiClock, FiActivity, FiBriefcase,
  FiCompass, FiTrendingDown, FiPieChart, FiArrowUpRight, FiSearch, FiSettings
} from 'react-icons/fi';
import { BsWhatsapp, BsBroadcast } from 'react-icons/bs';
import { MdOutlinePrecisionManufacturing } from 'react-icons/md';
import { FaCar, FaUserTie } from 'react-icons/fa';

import logo from '../../Images/logo.png';
import heroCar from '../../Images/hero-car.jpg';
import roviaVoice from '../../Images/rovia-voice.jpg';
import aimeeVideo from '../../Images/aimee-video.jpg';
import ecosystemImg from '../../Images/ecosystem.jpg';
import styles from './newLandingpage.module.css';
import BookDemoModal from '../BookDemoModal/BookDemoModal';

gsap.registerPlugin(ScrollTrigger);

const NewLandingPage = () => {
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const sectionRefs = useRef([]);
  const textRevealRefs = useRef([]);
  const tiltRefs = useRef([]);

  const addToSectionRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  const addToTextRefs = (el) => {
    if (el && !textRevealRefs.current.includes(el)) textRevealRefs.current.push(el);
  };

  useEffect(() => {
    // GSAP animations
    sectionRefs.current.forEach((el) => {
      if (!el) return;
      gsap.fromTo(el,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1.0,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });

    textRevealRefs.current.forEach((el) => {
      if (!el) return;
      gsap.fromTo(el,
        { opacity: 0, y: 80 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 90%'
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const handleMouseMove = (e, idx) => {
    const el = tiltRefs.current[idx];
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;

    gsap.to(el, {
      duration: 0.4,
      rotateY: x * 10,
      rotateX: -y * 10,
      ease: 'power2.out',
      transformPerspective: 1000,
    });
  };

  const handleMouseLeave = (idx) => {
    const el = tiltRefs.current[idx];
    if (!el) return;
    gsap.to(el, { duration: 0.6, rotateY: 0, rotateX: 0, ease: 'power2.out' });
  };

  return (
    <div id="home" className={styles.container}>

      {/* 🟦 1. HEADER / NAVIGATION */}
      <header className={styles.header} style={{ flex: "none" }}>
        <div className={styles.headerInner}>
          <div className={styles.logoArea} onClick={() => navigate('/')}>
            <img src={logo} alt="SaleSense Logo" className={styles.logoImg} />
            <span className={styles.logoText}>Sailsense <span className={styles.logoCyan}>AI</span></span>
          </div>
          <nav className={styles.navLinks}>
            <a href="#home">Home</a>
            <a href="/ai-agents" onClick={(e) => { e.preventDefault(); navigate('/ai-agents'); }}>AI Agents</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); navigate('/platform'); }}>Platform</a>
            <a href="/sales-operations" onClick={(e) => { e.preventDefault(); navigate('/sales-operations'); }}>Sales Operations</a>
            <a href="/solutions" onClick={(e) => { e.preventDefault(); navigate('/solutions'); }}>Solutions</a>
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
          </nav>
          <div className={styles.headerActions}>
            <button className={styles.trialBtn} onClick={() => navigate('/login')}>
              login <FiArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* 🟦 2. HERO SECTION WRAPPER */}
      <div className={styles.heroWrapper}>
        {/* Background grid line overlay - restricted to Hero Section */}
        <div className={styles.gridOverlay}></div>

        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.badgeCyan}>
              <span className={styles.pingDot}></span> Live - A product of Provaantech
            </div>

            <h1 className={styles.heroTitle}>
              Turn <br />
              Conversations <br />
              Into <span className={styles.heroTitleHighlight}>Conversions.</span> <br />
              At Scale.
            </h1>

            <p className={styles.heroDesc}>
              Sailsense AI captures leads, automates conversations and closes sales using AI Chat, Voice, Video and Automation agents — working 24/7 across every channel.
            </p>

            <div className={styles.heroButtons}>
              <button className={styles.heroBtnCyan} onClick={() => navigate('/signup')}>
                Start Free Trial <FiArrowRight />
              </button>
              <button className={styles.heroBtnDark} onClick={() => setIsDemoModalOpen(true)}>
                <FiPhone /> Talk to ROVIA Demo
              </button>
            </div>

            <div className={styles.heroInfoRow}>
              <div className={styles.heroInfoItem}>
                <FiCheck className={styles.heroInfoCheck} /> 24/7 Coverage
              </div>
              <div className={styles.heroInfoItem}>
                <FiCheck className={styles.heroInfoCheck} /> CRM Integrated
              </div>
              <div className={styles.heroInfoItem}>
                <FiCheck className={styles.heroInfoCheck} /> Multi-Channel
              </div>
            </div>
          </div>

          {/* Hero visual right side */}
          <div className={styles.heroVisual}>
            <div className={styles.glowFrame}>
              <div className={styles.glowFrameInner}>
                <img src={heroCar} alt="Dashboard Visual" className={styles.glowFrameImg} />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* 🟦 3. FOUR STATS HORIZONTAL BAR */}
      <section className={styles.statsRow}>
        <div className={styles.statsRowInner}>
          <div className={styles.statItem}>
            <div className={styles.statIconBox}><FiTrendingUp /></div>
            <div>
              <div className={styles.statVal}>3X</div>
              <div className={styles.statLabel}>More Lead Conversion</div>
            </div>
          </div>

          <div className={styles.statItem}>
            <div className={styles.statIconBox}><FiClock /></div>
            <div>
              <div className={styles.statVal}>80%</div>
              <div className={styles.statLabel}>Faster Response Time</div>
            </div>
          </div>

          <div className={styles.statItem}>
            <div className={styles.statIconBox}><FiCpu /></div>
            <div>
              <div className={styles.statVal}>24/7</div>
              <div className={styles.statLabel}>Automated Coverage</div>
            </div>
          </div>

          <div className={styles.statItem}>
            <div className={styles.statIconBox}><FiUsers /></div>
            <div>
              <div className={styles.statVal}>4+</div>
              <div className={styles.statLabel}>Industries Powered</div>
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 4. THE REALITY SECTION */}
      <section className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.realityGrid}>
          {/* Left Reality */}
          <div className={styles.realityLeft}>
            <div className={styles.badgeCyan}>The Reality</div>
            <h2 className={styles.sectionTitleMain}>
              If your leads aren't responding... you're already losing revenue.
            </h2>
            <p className={styles.sectionTextMain}>
              Every minute of delay in a sales conversation is a missed customer. Sailsense AI deploys fully automated agents that respond, qualify and convert — instantly.
            </p>
          </div>

          {/* Right reality lists */}
          <div className={styles.realityRight}>
            <div className={styles.realityProblemRow}>
              <span className={styles.redDot}></span> Incoming leads from ads, website, WhatsApp
            </div>
            <div className={styles.realityProblemRow}>
              <span className={styles.redDot}></span> Slow sales response time
            </div>
            <div className={styles.realityProblemRow}>
              <span className={styles.redDot}></span> Manual calling and follow-ups
            </div>
            <div className={styles.realityProblemRow}>
              <span className={styles.redDot}></span> Missed opportunities after office hours
            </div>
            <div className={styles.realitySolutionRow}>
              <FiCheck className={styles.greenCheck} /> Sailsense AI solves all of this with AI agents working 24/7.
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 5. PRIMARY FOCUS SECTION */}
      <section className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.sectionHeaderLeft}>
          <div className={styles.badgeCyan}>Primary Focus</div>
          <h2 className={styles.sectionTitleMain}>
            Built for Car Dealers, Showrooms & <br /> Automotive Networks
          </h2>
          <p className={styles.sectionTextMain}>
            In the automobile business, speed decides sales. Sailsense Automotive AI captures, qualifies and converts every lead — automatically.
          </p>
        </div>

        <div className={styles.automotiveGrid}>
          {/* Left Card */}
          <div className={styles.automotiveCardLeft}>
            <div className={styles.autoCardIcon}><FaCar /></div>
            <h3 className={styles.autoCardTitle}>Instant Lead Capture from Every Source</h3>
            <p className={styles.autoCardText}>Every lead enters your system instantly — no manual entry, no missed inquiries.</p>
            <div className={styles.autoCardList}>
              <div className={styles.autoCardListItem}><FiCheck className={styles.autoCardListItemCheck} /> Google Ads & Meta Ads</div>
              <div className={styles.autoCardListItem}><FiCheck className={styles.autoCardListItemCheck} /> Website Inquiry Forms</div>
              <div className={styles.autoCardListItem}><FiCheck className={styles.autoCardListItemCheck} /> WhatsApp Click-to-Chat</div>
              <div className={styles.autoCardListItem}><FiCheck className={styles.autoCardListItemCheck} /> Third-Party Car Listings</div>
            </div>
          </div>

          {/* Right Card */}
          <div className={styles.automotiveCardLeft}>
            <div className={styles.autoCardIcon}><FiZap /></div>
            <h3 className={styles.autoCardTitle}>From inquiry to test drive in minutes</h3>
            <p className={styles.autoCardText}>Automate the transition from visitor to qualified hot lead.</p>

            <div className={styles.timelineWrapper}>
              <div className={styles.timelineItem}>
                <div className={styles.timelineBadge}>00:00</div>
                <div className={styles.timelineText}>Lead fills form on website</div>
              </div>
              <div className={styles.timelineItem}>
                <div className={styles.timelineBadge}>00:08</div>
                <div className={styles.timelineText}>ROVIA calls the lead instantly</div>
              </div>
              <div className={styles.timelineItem}>
                <div className={styles.timelineBadge}>02:30</div>
                <div className={styles.timelineText}>Lead qualified & test drive booked</div>
              </div>
              <div className={styles.timelineItem}>
                <div className={styles.timelineBadge}>10:00</div>
                <div className={styles.timelineText}>Sales executive notified — hot lead</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 6. MEET ROVIA SECTION */}
      <section id="rovia" className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.roviaGrid}>
          {/* Left sphere visual */}
          <div className={styles.roviaVisual}>
            <div className={styles.sphereFrame}>
              <img src={roviaVoice} alt="ROVIA Voice Agent" className={styles.roviaVoiceImg} />
            </div>
          </div>

          {/* Right voice agent content */}
          <div className={styles.roviaRight}>
            <div className={styles.badgeCyan}>
              <FiPhone /> AI Voice Agent
            </div>
            <h2 className={styles.sectionTitleMain}>Meet ROVIA</h2>
            <p className={styles.sectionTextMain}>
              Your intelligent automotive calling assistant — built specifically for dealerships.
            </p>

            <div className={styles.roviaQuoteBox}>
              <div className={styles.roviaQuoteHeader}>
                <FiMic /> Live Call Sample
              </div>
              <p className={styles.roviaQuoteText}>
                "Hi, this is ROVIA from XYZ Motors. Are you looking for a new car purchase this week, or just exploring options?"
              </p>
            </div>

            <div className={styles.roviaFeaturesSplit}>
              <div className={styles.roviaCheckList}>
                <div className={styles.roviaCheckItem}>
                  <FiCheck className={styles.greenCheck} /> Calls every new lead within seconds
                </div>
                <div className={styles.roviaCheckItem}>
                  <FiCheck className={styles.greenCheck} /> Qualifies leads automatically
                </div>
                <div className={styles.roviaCheckItem}>
                  <FiCheck className={styles.greenCheck} /> Follow-up calls for cold leads
                </div>
              </div>

              <div className={styles.roviaCheckList}>
                <div className={styles.roviaCheckItem}>
                  <FiCheck className={styles.greenCheck} /> Asks customer requirements: budget, model, fuel, variant
                </div>
                <div className={styles.roviaCheckItem}>
                  <FiCheck className={styles.greenCheck} /> Schedules test drives in real time
                </div>
                <div className={styles.roviaCheckItem}>
                  <FiCheck className={styles.greenCheck} /> Routes hot leads to human sales executives
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 7. CHAT AI SECTION */}
      <section className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.chatbotGrid}>
          {/* Left side Chat dialog bubbles */}
          <div className={styles.chatbotLeft}>
            <div className={styles.badgeCyan}>Chat AI</div>
            <h2 className={styles.sectionTitleMain}>
              Sailsense Chatbot for Website + WhatsApp
            </h2>
            <p className={styles.sectionTextMain}>
              Works even when your showroom is closed. Captures every conversation and turns it into a qualified lead.
            </p>

            <div className={styles.chatDialogBox}>
              <div className={styles.chatBubbleRight}>
                Looking for the price of the new SUV variant
              </div>
              <div className={styles.chatBubbleLeft}>
                Sure! The new variant starts at ₹14.5L. EMI from ₹19,800/mo. Want to book a free test drive?
              </div>
              <div className={styles.chatBubbleRight}>
                Yes, Saturday 11 AM
              </div>
              <div className={styles.chatBubbleLeft}>
                Booked ✓ Confirmation sent on WhatsApp. ROVIA will call to confirm.
              </div>
            </div>
          </div>

          {/* Right side 2x2 cards */}
          <div className={styles.chatbotRight}>
            <div className={styles.chatbotFeatureCard}>
              <FiMessageCircle className={styles.chatbotFeatIcon} />
              <h4 className={styles.chatbotFeatTitle}>Instant Replies</h4>
              <p className={styles.chatbotFeatText}>Answers car inquiries the moment they arrive.</p>
            </div>

            <div className={styles.chatbotFeatureCard}>
              <FiBarChart2 className={styles.chatbotFeatIcon} />
              <h4 className={styles.chatbotFeatTitle}>Variant & Pricing</h4>
              <p className={styles.chatbotFeatText}>Explains models, EMIs and finance options clearly.</p>
            </div>

            <div className={styles.chatbotFeatureCard}>
              <FiTarget className={styles.chatbotFeatIcon} />
              <h4 className={styles.chatbotFeatTitle}>Test Drive Booking</h4>
              <p className={styles.chatbotFeatText}>Books test drives directly inside the chat window.</p>
            </div>

            <div className={styles.chatbotFeatureCard}>
              <FiRefreshCw className={styles.chatbotFeatIcon} />
              <h4 className={styles.chatbotFeatTitle}>CRM Sync</h4>
              <p className={styles.chatbotFeatText}>Auto-captures lead data into your CRM, no manual work.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 8. MEET AIMEE SECTION */}
      <section id="aimee" className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.aimeeGrid}>
          {/* Left features info */}
          <div className={styles.aimeeLeftContent}>
            <div className={styles.badgeCyan}>
              <FiVideo /> AI Video Agent
            </div>
            <h2 className={styles.sectionTitleMain}>Meet AIMEE</h2>
            <p className={styles.sectionTextMain}>
              Your digital sales presenter — a virtual showroom executive that greets, explains and converts.
            </p>

            <div className={styles.aimeeListVertical}>
              <div className={styles.aimeeListItem}>
                <FiCheck className={styles.greenCheck} /> Greets website visitors with a personal video welcome
              </div>
              <div className={styles.aimeeListItem}>
                <FiCheck className={styles.greenCheck} /> Explains cars, offers and finance plans on demand
              </div>
              <div className={styles.aimeeListItem}>
                <FiCheck className={styles.greenCheck} /> Acts as a virtual showroom executive online
              </div>
              <div className={styles.aimeeListItem}>
                <FiCheck className={styles.greenCheck} /> Guides users toward enquiry & booking
              </div>
            </div>
          </div>

          {/* Right Aimee visual card */}
          <div className={styles.aimeeRightCard}>
            <div className={styles.aimeeCard}>
              <div className={styles.aimeeCardInner}>
                <div className={styles.aimeeCardGlow}></div>
                <img
                  src={aimeeVideo}
                  alt="AIMEE Presenter"
                  className={styles.aimeeCardImg}
                />
                <div className={styles.aimeeOverlayInfo}>
                  <div className={styles.aimeeOverlayBadge}>
                    <span className={styles.aimeeOverlayBadgeDot}></span> Live • AIMEE
                  </div>
                  <h4 className={styles.aimeeOverlayText}>"Hi! Let me show you what's new this week."</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 9. AUTOMATION ENGINE SECTION */}
      <section id="automation-engine" className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.sectionCenter}>
          <div className={styles.sectionCenterBadge}>
            <FiCpu /> Automation Engine
          </div>
          <h2 className={styles.sectionCenterTitle}>Never miss a lead. Ever again.</h2>
        </div>

        <div className={styles.automationSectionGrid}>
          {/* Left Column */}
          <div className={styles.autoColLeft}>
            <span className={styles.columnHeader}>If a customer...</span>
            <div className={styles.autoColLeft}>
              {["Fills a form", "Leaves WhatsApp chat", "Misses a call", "Doesn't respond"].map((item, idx) => (
                <div key={idx} className={styles.flowItem}>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Middle Node */}
          <div className={styles.autoColMiddle}>
            <div className={styles.middleNode}>
              <div className={styles.middleNodePulse}></div>
              <FiZap className={styles.middleNodeIcon} />
            </div>
          </div>

          {/* Right Column */}
          <div className={styles.autoColRight}>
            <span className={styles.columnHeader}>Sailsense triggers...</span>
            <div className={styles.autoColRight}>
              {[
                { text: "Follow-up calls via ROVIA", icon: FiPhone },
                { text: "WhatsApp reminders", icon: FiMessageCircle },
                { text: "Email sequences", icon: FiMail },
                { text: "Sales alerts to your team", icon: FiZap }
              ].map((item, idx) => (
                <div key={idx} className={styles.flowItemRight}>
                  <item.icon className={styles.flowIcon} />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 10. THE SAILSENSE AI ECOSYSTEM */}
      <section id="ecosystem" className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.sectionCenter}>
          <div className={styles.sectionCenterBadge}>
            One Platform. Complete Sales System.
          </div>
          <h2 className={styles.sectionCenterTitle}>The Sailsense AI Ecosystem</h2>
        </div>

        <div className={styles.ecoDiagramBox}>
          <div className={styles.ecoDiagramInner} style={{ padding: 0 }}>
            <img src={ecosystemImg} alt="Sailsense AI Ecosystem Diagram" className={styles.ecoDiagramImg} />
          </div>
        </div>

        {/* 4 columns list */}
        <div className={styles.ecoColumnsGrid}>
          {[
            { step: "01", title: "Attract", icon: FiTarget, items: ["Ads", "Website", "Portals", "Social"] },
            { step: "02", title: "Engage", icon: FiMessageCircle, items: ["Chatbot", "WhatsApp", "ROVIA Voice", "AIMEE Video"] },
            { step: "03", title: "Convert", icon: FiZap, items: ["Lead scoring", "Smart follow-ups", "Test drive scheduling", "Sales handover"] },
            { step: "04", title: "Analyze", icon: FiBarChart2, items: ["Conversion tracking", "Source performance", "Team analytics", "Insights"] }
          ].map((column, idx) => (
            <div key={idx} className={styles.ecoColCard}>
              <div className={styles.ecoColHeader}>
                <span className={styles.ecoStep}>{column.step}</span>
                <column.icon className={styles.ecoStepIcon} />
              </div>
              <h4 className={styles.ecoColTitle}>{column.title}</h4>
              <div className={styles.ecoColList}>
                {column.items.map((item, itemIdx) => (
                  <div key={itemIdx} className={styles.ecoColListItem}>
                    <span className={styles.ecoBullet}></span> {item}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>



      {/* 🟦 12. CORE PLATFORM CAPABILITIES */}
      <section className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.sectionCenter}>
          <div className={styles.sectionCenterBadge}>
            Core Platform Capabilities
          </div>
          <h2 className={styles.sectionCenterTitle}>Everything your sales team needs. Automated.</h2>
        </div>

        <div className={styles.coreCapsGrid}>
          {[
            "Real-time lead response system",
            "AI Chat + Voice + Video agents",
            "ROVIA AI calling engine",
            "WhatsApp automation workflows",
            "CRM integrations out of the box",
            "Sales tracking dashboard",
            "Smart follow-up automation",
            "Self-learning conversation engine"
          ].map((capability, idx) => {
            const isSpecial = capability === "Self-learning conversation engine";
            return (
              <div
                key={idx}
                className={isSpecial ? styles.capCardSpecial : styles.capCard}
              >
                <div className={styles.capCardLeft}>
                  <div className={styles.capCheck}><FiCheck /></div>
                  <span>{capability}</span>
                </div>
                {isSpecial && <span className={styles.capBadge}>Active</span>}
              </div>
            );
          })}
        </div>
      </section>

      {/* 🟦 13. OUTCOMES SECTION */}
      <section className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.sectionCenter}>
          <div className={styles.sectionCenterBadge}>
            Why Businesses Choose Sailsense
          </div>
          <h2 className={styles.sectionCenterTitle}>Outcomes that move revenue, not dashboards.</h2>
        </div>

        <div className={styles.outcomesRowGrid}>
          {[
            { stat: "3X", title: "More lead conversion" },
            { stat: "80%", title: "Faster response time" },
            { stat: "60%", title: "Reduced manpower dependency" },
            { stat: "24/7", title: "Automated sales coverage" }
          ].map((item, idx) => (
            <div key={idx} className={styles.outcomeBoxCard}>
              <div className={styles.outcomeBoxCardStat}>{item.stat}</div>
              <div className={styles.outcomeBoxCardTitle}>{item.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 🟦 14. BOTTOM CTA */}
      <section className={styles.sectionBlock} ref={addToSectionRefs}>
        <div className={styles.ctaPanel}>
          <span className={styles.ctaBadge}>Your AI sales team awaits</span>
          <h2 className={styles.ctaHeading}>Don't just collect leads. <br />Convert them automatically.</h2>
          <p className={styles.ctaDesc}>
            Sailsense AI gives you a full AI sales team — Chat, Voice and Video — that never sleeps.
          </p>

          <div className={styles.ctaButtons}>
            <button className={styles.heroBtnCyan} onClick={() => navigate('/login')}>
              See your dashboard
            </button>
            <button className={styles.heroBtnDark} onClick={() => setIsDemoModalOpen(true)}>
              Talk to ROVIA Demo
            </button>
            <button className={styles.heroBtnDark} onClick={() => setIsDemoModalOpen(true)}>
              See AIMEE Demo
            </button>
          </div>
        </div>
      </section>

      {/* 🟦 15. FOOTER */}
      <footer id="footer" className={styles.footerSection}>
        <div className={styles.footerInner}>
          {/* Brand area left */}
          <div className={styles.footerBrandColumn}>
            <div className={styles.footerBrandLogo} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src={logo} alt="SaleSense Logo" className={styles.footerLogoImg} />
              <span>Sailsense AI</span>
            </div>
            <p className={styles.footerBrandText}>
              AI Chat, Voice and Video agents that capture leads, automate conversations and close sales — 24/7.
            </p>
            <p className={styles.footerBrandProductOf}>
              A product of <strong>Provaantech</strong>
            </p>
          </div>

          {/* Brand System center */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Brand System</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>
                <FiMessageCircle style={{ marginRight: '0.5rem' }} /> Sailsense Chatbot
              </li>
              <li className={styles.footerLinkItem}>
                <FiPhone style={{ marginRight: '0.5rem' }} /> ROVIA — Voice AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiVideo style={{ marginRight: '0.5rem' }} /> AIMEE — Video AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiSettings style={{ marginRight: '0.5rem' }} /> Sailsense Platform
              </li>
            </ul>
          </div>

          {/* Industries right */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Focus Industry</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>Automotive Dealerships</li>
            </ul>
          </div>
        </div>

        {/* Bottom row copyright */}
        <div className={styles.footerBottomBar}>
          <div>© 2026 Sailsense AI - Provaantech. All rights reserved.</div>
          <div>Built for high-performance sales teams.</div>
        </div>
      </footer>
      <BookDemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </div>
  );
};

export default NewLandingPage;
